export interface TreeNode {
  number: string;
  heading: string | null;
  kind: string;
  depth: number;
  isContainer: boolean;
  hasText: boolean;
  /** Opening words, for the subclauses the agreement never titled. */
  snippet: string | null;
  badges: {
    brokenRef: number;
    annotations: number;
    questions: number;
    claims: number;
    protect: boolean;
    silent: boolean;
  };
}

/** One renderable passage of the agreement. */
export interface Passage {
  number: string;
  heading: string | null;
  kind: string;
  depth: number;
  textVerbatim: string;
  pageStart: number | null;
}

export interface Defect {
  kind: string;
  label: string | null;
  detail: string;
  evidence: string | null;
  page: number | null;
  confirmed: boolean;
}

export interface Formula {
  label: string;
  expression: string | null;
  variables: Record<string, string>;
  recoveredFrom: string | null;
  note: string | null;
  verifiedAt: string | null;
}

export interface ClauseDetail {
  number: string;
  heading: string | null;
  kind: string;
  depth: number;
  instrument: string;
  textVerbatim: string;
  textPlain: string | null;
  plainState: string;
  isContainer: boolean;
  pageStart: number | null;
  isProtect: boolean;
  isSilent: boolean;
  children: { number: string; heading: string | null; hasText: boolean }[];
  /**
   * The provision this clause belongs to, and its number and heading. Null for
   * a Part or schedule heading, which are navigation rather than provisions.
   */
  provision: { number: string; heading: string | null; len: number } | null;
  /** Every node of the provision, in document order. */
  descendants: Passage[];
  /** Defects in the agreement — bargaining material. */
  drafting: Defect[];
  /** Defects in our copy of it — a warning, not a claim. */
  artefacts: Defect[];
  /** Formulas lifted out of prose the text layer could not carry. */
  formulas: Formula[];
  refs: {
    outbound: {
      toNumber: string;
      status: string;
      kind: string;
      suggestedFix: string | null;
      context: string | null;
    }[];
    inbound: { number: string; heading: string | null }[];
  };
  leverage: {
    ask: string | null;
    precedent: string | null;
    pressure: string | null;
    watch: string | null;
    draftedBy: string;
    verifiedAt: string | null;
  } | null;
  claims: { ref: string; title: string; type: string; status: string }[];
}


export interface PaperClaim {
  ref: string;
  title: string;
  type: string;
  status: string;
  category: string | null;
  verbatim: string | null;
  demand: string | null;
  rationale: string | null;
  cites: string | null;
  owner: string | null;
  wordingChecked: boolean;
}

export interface PaperProtect {
  number: string;
  heading: string | null;
  exposure: string;
  verified: boolean;
  note: string | null;
  awardNumber: string | null;
  awardHeading: string | null;
  verbatim: string | null;
}

export interface ClaimsPaperDoc {
  claims: PaperClaim[];
  protect: PaperProtect[];
  watermark: string;
  preparedBy: string | null;
}

export interface Identity {
  userId: string;
  displayName: string;
  role: string;
}

/** One place this person is signed in. Deliberately no location. */
export interface Device {
  id: string;
  userAgent: string | null;
  lastSeenAt: string;
  createdAt: string;
  expiresAt: string;
  /** The session behind this very request, compared by hash server-side. */
  isCurrent: boolean;
}

/**
 * A participant, as the roster shows them.
 *
 * `seat` is a placeholder with no address — one of the seven representative
 * slots, holding a colour and whatever the seeding pipeline attributed to it,
 * waiting for a real person to take it over.
 */
export interface Person {
  id: string;
  displayName: string;
  initials: string;
  colourToken: string | null;
  role: string;
  status: 'active' | 'invited' | 'seat';
}

/** Everything the context dock shows beside a clause. */
export interface DockData {
  clause: { number: string; heading: string | null };
  neighbourhood: {
    parent: { number: string; heading: string | null } | null;
    children: { number: string; heading: string | null; kind: string; hasText: boolean }[];
    outbound: {
      toNumber: string;
      status: string;
      kind: string;
      suggestedFix: string | null;
      toHeading: string | null;
    }[];
    inbound: { number: string; heading: string | null }[];
  };
  activity: {
    questions: { id: string; text: string; status: string; createdAt: string }[];
    claims: { ref: string; title: string; type: string; status: string }[];
    leverage: {
      ask: string | null;
      precedent: string | null;
      pressure: string | null;
      watch: string | null;
      draftedBy: string;
      verifiedAt: string | null;
    } | null;
    marks: number;
  };
  comparators: {
    kind: string;
    label: string;
    note: string | null;
    passages: {
      number: string;
      heading: string | null;
      instrument: string;
      text: string;
      truncated: boolean;
      pageStart: number | null;
    }[];
  }[];
}

/** A row of the questions ledger. */
export interface LedgerQuestion {
  id: string;
  text: string;
  status: string;
  isAnonymous: boolean;
  isMine: boolean;
  createdAt: string;
  raisedBy: string | null;
  raisedByColour: string | null;
  clauses: { number: string; heading: string | null }[];
  answer: {
    body: string;
    source: string;
    citations: { number: string }[];
    draftedBy: string;
    verifiedAt: string | null;
  } | null;
}

/** A row of the claims ledger. */
export interface LedgerClaim {
  id: string;
  ref: string;
  title: string;
  category: string | null;
  type: string;
  status: string;
  priorityRank: number | null;
  costMethod: string | null;
  owner: string | null;
  ownerColour: string | null;
  /** 'seed' means mockup fixture, not a colleague's work. */
  draftedBy: DraftedBy;
  clauses: { number: string; relation: string }[];
  redTeamItems: number;
}

/** A row of the decisions ledger. */
export interface LedgerDecision {
  id: string;
  occurredOn: string;
  subject: string;
  resolution: string;
  method: string | null;
  supersedes: string | null;
  supersedesSubject: string | null;
  supersedesDate: string | null;
  loggedBy: string;
  actionedBy: string | null;
}

/** One claim, whole: the workbench object. */
export interface ClaimDetail {
  id: string;
  ref: string;
  title: string;
  category: string | null;
  type: string;
  status: string;
  priorityRank: number | null;
  currentVerbatim: string | null;
  proposedWording: string | null;
  /** The machine draft the wording came from, kept beside it. Null means a person wrote it. */
  proposedWordingAi: string | null;
  wordingVerifiedAt: string | null;
  wordingVerifiedBy: string | null;
  rationale: string | null;
  costPerFteYear: number | null;
  costMethod: string | null;
  costConfidence: string | null;
  draftedBy: string;
  updatedAt: string;
  owner: string | null;
  ownerColour: string | null;
  clauses: {
    number: string;
    relation: string;
    heading: string | null;
    textVerbatim: string | null;
  }[];
  comparators: {
    id: string;
    quote: string;
    citation: string;
    note: string | null;
    instrument: string;
  }[];
  positions: {
    id: string;
    occurredOn: string;
    position: string;
    verbatimQuote: string | null;
    source: string | null;
    loggedBy: string;
  }[];
  objections: ClaimObjection[];
}

/**
 * The one fetch wrapper.
 *
 * Everything goes through here so cross-cutting behaviour exists once. Two
 * defects lived in the four hand-rolled copies this replaces: a bodyless POST
 * still sent `content-type: application/json`, which Fastify rejects with a
 * 400 before the handler runs — so sign-out, resend and verify-wording could
 * never succeed from a browser — and GET threw the raw status while writes
 * threw the server's message, so read errors bypassed the UX writes were
 * given.
 */
async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`/api${path}`, {
    method,
    // The session lives in an HttpOnly cookie, never readable from script —
    // which is the point, and why every call opts into sending it.
    credentials: 'same-origin',
    /*
     * FormData goes through untouched. Stringifying it produces "[object
     * FormData]", and naming the content type ourselves produces a multipart
     * boundary the server cannot find — the browser has to set both.
     */
    ...(body === undefined
      ? {}
      : body instanceof FormData
        ? { body }
        : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? `${path} failed: ${response.status}`);
  }
  return payload;
}

/**
 * Exported so surfaces can reuse the one wrapper.
 *
 * calculators/api.tsx and drafts/api.tsx each carried a byte-identical copy of
 * `request`, written while several agents held the tree and this file belonged
 * to one of them. Two copies of the code that decides what a non-2xx means is
 * two places to fix when that contract changes, and the copies would have
 * drifted the first time it did.
 */
export { request };


export type SurveySection = import('@tmea/shared').Section;

export interface SurveyResults {
  title: string;
  total: number;
  aggregate: Record<string, {
    kind: string;
    answered: number;
    median?: number | null;
    mean?: number | null;
    min?: number | null;
    max?: number | null;
    counts?: Record<string, number>;
    texts?: string[];
  }>;
  responses: { id: string; role: string; submittedOn: string; answers: Record<string, unknown> }[];
  sections: SurveySection[];
}

export interface AskAttachment {
  id: string;
  filename: string;
  mediaType: string;
  /** Length of the document before any truncation. */
  chars: number;
  truncated: boolean;
}

export interface AskThreadRow {
  id: string;
  opening: string;
  /** The generated name, or the opening question when there is none. */
  label: string;
  named: boolean;
  visibility: 'private' | 'team' | 'cohort';
  updatedAt: string;
  /** Whether this is the reader's own thread or one shared with them. */
  mine: boolean;
  author: string;
  turns: number;
  claims: number;
}

export interface AskThreadTurn {
  id: string;
  ordinal: number;
  question: string;
  answer: string | null;
  drafted: string | null;
  model: string | null;
  citations: string[];
  becameClaim: string | null;
}

const get = <T,>(path: string): Promise<T> => request<T>('GET', path);
const post = <T,>(path: string, body?: unknown): Promise<T> => request<T>('POST', path, body);
const patch = <T,>(path: string, body: unknown): Promise<T> => request<T>('PATCH', path, body);
const del = (path: string): Promise<{ ok: true }> => request('DELETE', path);

/** One row awaiting a human, whatever kind of row it is. */
export interface QueueItem {
  entityTable: string;
  entityId: string;
  clauseNumber: string | null;
  clauseHeading: string | null;
  title: string;
  fields: { label: string; value: string | null }[];
  model: string | null;
  draftedAt: string | null;
  /**
   * What a person decided about this same subject before.
   *
   * Not a signed-off state — the row on screen is a different draft from a
   * different model. It is prior reasoning, shown so it does not have to be
   * reconstructed. See migration 0033 for why these came loose.
   */
  priorReviews: {
    verdict: string;
    note: string | null;
    reviewedAt: string;
    draftedVerdict: string | null;
    draftedNote: string | null;
    correctedVerdict: string | null;
    correctedNote: string | null;
  }[];
}

export type Verb = 'highlight' | 'note' | 'question' | 'ask' | 'compare' | 'claim';

/** A mark as the server reports it. */
export interface Annotation {
  id: string;
  /** Which node of the reading block the mark sits on. */
  clauseNumber: string;
  verb: Verb;
  motivation: string;
  body: string | null;
  exact: string;
  prefix: string | null;
  suffix: string | null;
  start: number;
  end: number;
  visibility: 'private' | 'team' | 'cohort';
  isAnonymous: boolean;
  isMine: boolean;
  /** Null when the mark is anonymous. The server never sends it otherwise. */
  authorName: string | null;
  authorInitials: string | null;
  authorColour: string | null;
  createdAt: string;
  publishedAt: string | null;
  /**
   * Settled, not deleted. A clause argued over and answered used to look
   * identical to one nobody had touched, because the only way to clear a mark
   * was to remove it — which throws away the reasoning that settled it.
   */
  resolvedAt: string | null;
  resolutionNote: string | null;
  resolvedByName: string | null;
  replies: number;
}

/** A mark after its anchor has been resolved against the current text. */
export interface PlacedAnnotation extends Annotation {
  /** True when the words it was attached to are no longer in the clause. */
  orphaned: boolean;
}

export interface Reply {
  id: string;
  body: string;
  createdAt: string;
  isAnonymous: boolean;
  isMine: boolean;
  authorName: string | null;
  authorColour: string | null;
}

export const api = {
  tree: () => get<TreeNode[]>('/tree'),
  clause: (number: string) => get<ClauseDetail>(`/clause/${encodeURIComponent(number)}`),
  instruments: () =>
    get<{ shortName: string; kind: string; clauses: string; isPrimary: boolean }[]>('/instruments'),

  dock: (number: string) => get<DockData>(`/clause/${encodeURIComponent(number)}/dock`),

  me: () => get<{ identity: Identity | null }>('/auth/me'),
  sessions: () => get<{ sessions: Device[] }>('/auth/sessions'),
  revokeSession: (id: string) => del(`/auth/sessions/${id}`),

  people: () => get<{ people: Person[]; freeColours: number }>('/people'),
  invite: (body: { email: string; displayName: string; role: string }) =>
    post<{ ok: true }>('/people/invite', body),
  giveSeat: (id: string, body: { email: string; displayName?: string }) =>
    post<{ ok: true }>(`/people/${id}/address`, body),
  resend: (id: string) => post<{ ok: true }>(`/people/${id}/resend`),
  claimColour: (id: string) => post<{ ok: true }>(`/people/${id}/colour`),
  retire: (id: string) => del(`/people/${id}`),

  annotations: (clause: string) =>
    get<{ annotations: Annotation[] }>(`/clause/${encodeURIComponent(clause)}/annotations`),
  annotate: (body: {
    clause: string;
    verb: Verb;
    body?: string | undefined;
    exact: string;
    prefix: string;
    suffix: string;
    start: number;
    end: number;
    isAnonymous: boolean;
    visibility: 'private' | 'team' | 'cohort';
  }) => post<{ id: string }>('/annotations', body),
  updateAnnotation: (id: string, body: { visibility?: string; body?: string }) =>
    patch<{ ok: true }>(`/annotations/${id}`, body),
  /**
   * Settles someone's mark — not necessarily your own, which is the point.
   * The note is required by the route, so an empty one is a 400 rather than a
   * silently unexplained resolution.
   */
  resolveAnnotation: (id: string, note: string) =>
    patch<{ ok: true }>(`/annotations/${id}/resolve`, { resolved: true, note }),
  reopenAnnotation: (id: string) =>
    patch<{ ok: true }>(`/annotations/${id}/resolve`, { resolved: false }),
  deleteAnnotation: (id: string) => del(`/annotations/${id}`),
  replies: (id: string) => get<{ replies: Reply[] }>(`/annotations/${id}/replies`),
  reply: (id: string, body: { body: string; isAnonymous: boolean }) =>
    post<{ id: string }>(`/annotations/${id}/replies`, body),
  unpublished: () => get<{ unpublished: number }>('/annotations/unpublished'),

  /** The written answers, grouped. C4's suggestion rail and its offline fallback. */
  answers: () =>
    get<{ groups: { label: string; items: BankedAnswer[] }[]; total: number }>('/answers'),

  ledgerQuestions: () => get<{ questions: LedgerQuestion[] }>('/ledgers/questions'),
  raiseQuestion: (body: { text: string; isAnonymous: boolean; clauses: string[] }) =>
    post<{ id: string }>('/ledgers/questions', body),
  triageQuestion: (id: string, status: string) =>
    patch<{ ok: true }>(`/ledgers/questions/${id}`, { status }),
  ledgerClaims: () => get<{ claims: LedgerClaim[] }>('/ledgers/claims'),
  updateClaim: (id: string, body: { status?: string; priorityRank?: number | null }) =>
    patch<{ ok: true }>(`/ledgers/claims/${id}`, body),
  ledgerDecisions: () => get<{ decisions: LedgerDecision[] }>('/ledgers/decisions'),

  claim: (id: string) => get<ClaimDetail>(`/claims/${id}`),
  createClaim: (body: { title: string; type: string; clause: string; verbatim: string }) =>
    post<{ id: string; ref: string }>('/claims', body),
  updateClaim2: (
    id: string,
    body: {
      title?: string;
      proposedWording?: string | null;
      proposedWordingAi?: string | null;
      rationale?: string | null;
      costPerFteYear?: number | null;
      costMethod?: string | null;
      costConfidence?: 'low' | 'medium' | 'high' | null;
    },
  ) => patch<{ ok: true }>(`/claims/${id}`, body),
  verifyWording: (id: string) => post<{ ok: true }>(`/claims/${id}/verify-wording`),
  logPosition: (
    id: string,
    body: { occurredOn: string; position: string; verbatimQuote?: string; source?: string },
  ) => post<{ id: string }>(`/claims/${id}/positions`, body),

  // ─── Objections. Two calls, because raising and rating are two acts: one
  // anticipates the employer and costs nothing, the other is the group
  // deciding how much trouble it is in, and only the second is what the
  // endorsement trigger from migration 0013 reads. `rateObjection` is defined
  // below the object for the same reason `exportSelection` is — its 422 is a
  // judgement and has to survive as a value.
  raiseObjection: (id: string, body: { objection: string; counter?: string }) =>
    post<{ id: string }>(`/claims/${id}/objections`, body),
  rateObjection,
  requestLink: (email: string) => post<{ ok: true }>('/auth/request-link', { email }),
  exchange: (token: string) => post<{ identity: Identity }>('/auth/session', { token }),
  signOut: () => post<{ ok: true }>('/auth/sign-out'),

  /** What the pipeline has drafted and nobody has checked. */
  verifyQueue: (sampleOnly = false): Promise<{ items: QueueItem[]; total: number; shown: number }> =>
    get(`/verify/queue${sampleOnly ? '?sample=1' : ''}`),

  /** Corrections captured so far — the eval set, as it stands. */
  verifyEvals: (): Promise<{
    corrections: unknown[];
    tally: Record<string, number>;
  }> => get('/verify/evals'),

  review: (body: {
    entityTable: string;
    entityId: string;
    verdict: 'accepted' | 'corrected' | 'rejected';
    corrected?: Record<string, unknown> | null;
    note?: string;
  }): Promise<{ ok: true }> => post('/verify/review', body),

  /** Twenty representative rows, and what reviewing them has measured so far. */
  sample: (): Promise<{
    items: { stratum: string; entityTable: string; entityId: string; clauseNumber: string; title: string }[];
    strata: { key: string; label: string; why: string; want: number; got: number }[];
    measured: {
      reviewed: number;
      wrong: number;
      errorRate: number | null;
      byKind: { entityTable: string; accepted: number; corrected: number; rejected: number }[];
    };
  }> => get('/verify/sample'),

  /**
   * What was decided without a person in the loop, and the controls to undo it.
   *
   * Named `adjudications` rather than `secondOpinions` because the table name
   * describes where the row came from and this describes what it does: these
   * settle comparisons now, they are not advisory annotations on somebody
   * else's decision.
   */
  adjudications: (): Promise<AdjudicationReport> => get('/adjudications'),
  windBack: (id: string, note: string): Promise<{ ok: true }> =>
    post(`/adjudications/${id}/wind-back`, { note }),
  restoreAdjudication: (id: string): Promise<{ ok: true }> =>
    post(`/adjudications/${id}/restore`),
  windBackAll: (note: string): Promise<{ ok: true; woundBack: number }> =>
    post('/adjudications/wind-back-all', { note }),

  compareIndex: (): Promise<CompareIndexRow[]> => get('/compare'),
  compare: (number: string): Promise<ComparisonView> =>
    get(`/compare/${encodeURIComponent(number)}`),
  equivalence: (): Promise<Equivalence> => get('/compare/equivalence'),

  /**
   * Lexical search over clause text. No embedding, no model, no cost.
   *
   * Distinct from `ask` on purpose: Ask answers a question and pays for prose;
   * this finds where the words are and returns in milliseconds, which is what
   * makes it usable as somebody types.
   */
  search: (q: string, scope: 'primary' | 'all' = 'primary'): Promise<SearchResults> =>
    get(`/search?q=${encodeURIComponent(q)}&scope=${scope}`),

  /**
   * A question, and the conversation it belongs to.
   *
   * `threadId` is what makes a follow-up mean anything: without it every
   * question was sent to the model alone, so "how does this compare to the
   * public system" had no "this" and retrieved on a bare pronoun.
   */
  ask: (question: string, threadId?: string | null): Promise<AskAnswer> =>
    post('/ask', { question, threadId: threadId ?? null }),

  /**
   * Threads, so a follow-up can mean something.
   *
   * The answer is fetched by `ask` as before and then recorded, rather than the
   * thread endpoint making its own model call. That keeps one grounding path
   * instead of two — the alternative was duplicating the retrieval and citation
   * contract in a second route, which is how two answers to the same question
   * start disagreeing.
   */
  startThread: (turn: TurnInput): Promise<{ threadId: string; turnId: string }> =>
    post('/ask/threads', turn),
  addTurn: (threadId: string, turn: TurnInput): Promise<{ turnId: string; ordinal: number }> =>
    post(`/ask/threads/${threadId}/turns`, turn),

  /** Names a conversation from its first question. Safe to call more than once. */
  nameThread: (id: string): Promise<{ title: string | null }> =>
    post(`/ask/threads/${id}/title`),

  /** Opens a conversation with no turns, so a document can be attached first. */
  openThread: (opening: string): Promise<{ threadId: string }> =>
    post('/ask/threads/open', { opening }),

  /**
   * A document attached to a conversation.
   *
   * FormData rather than JSON, and no content-type header: the browser sets it
   * with the multipart boundary, and naming it ourselves produces a boundary
   * the server cannot find.
   */
  attach: (
    threadId: string,
    file: File,
  ): Promise<{ id: string; filename: string; chars: number; truncated: boolean }> => {
    const form = new FormData();
    form.append('file', file);
    return request('POST', `/ask/threads/${threadId}/attachments`, form);
  },

  attachments: (threadId: string): Promise<{ attachments: AskAttachment[] }> =>
    get(`/ask/threads/${threadId}/attachments`),

  detach: (threadId: string, id: string): Promise<{ removed: boolean }> =>
    request('DELETE', `/ask/threads/${threadId}/attachments/${id}`),

  /** The survey questions. Public — no session required. */
  survey: (slug: string): Promise<{
    title: string;
    preamble: string | null;
    open: boolean;
    sections: SurveySection[];
  }> => get(`/survey/${slug}`),

  /** A response. Public, and there is no route to read one back. */
  submitSurvey: (slug: string, role: string, answers: Record<string, unknown>): Promise<{ recorded: boolean }> =>
    post(`/survey/${slug}/responses`, { role, answers }),

  /** Everything that came back. Representatives only, by policy. */
  surveyResponses: (slug: string): Promise<SurveyResults> => get(`/survey/${slug}/responses`),

  /** A narrative over the responses. Generated on request, never stored. */
  surveyNarrative: (slug: string): Promise<{ narrative: string | null; responses: number; model?: string }> =>
    post(`/survey/${slug}/narrative`),

  /** A claim from what the staff said, carrying the evidence and its limits. */
  claimFromSurvey: (
    slug: string,
    body: { title: string; type: string; evidence: string },
  ): Promise<{ ref: string }> => post(`/survey/${slug}/claims`, body),

  /** Every thread this rep can see: their own, and any the other one shared. */
  askThreads: (): Promise<{ threads: AskThreadRow[] }> => get('/ask/threads'),

  /** One thread's exchange, in order. */
  askThread: (id: string): Promise<{ turns: AskThreadTurn[] }> => get(`/ask/threads/${id}`),

  shareThread: (id: string, visibility: 'private' | 'team'): Promise<{ visibility: string }> =>
    patch(`/ask/threads/${id}`, { visibility }),

  /** Keeps an answer where the clause is, as a note the reading surfaces show. */
  turnToNote: (threadId: string, turnId: string): Promise<{ annotationId: string; clause: string }> =>
    post(`/ask/threads/${threadId}/turns/${turnId}/note`),

  /** The bridge: an exchange becomes a claim, carrying its citations. */
  claimFromTurn: (
    turnId: string,
    body: { title: string; type: string; relation?: string },
  ): Promise<{ ref: string; id: string; clauses: number; exportBlocked: boolean }> =>
    post(`/ask/turns/${turnId}/claim`, body),

  /** Claims the corpus has already made the case for. */
  suggestions: (): Promise<SuggestionList> => get('/suggestions'),

  plain: (): Promise<PlainData> => get('/plain'),

  room: (): Promise<RoomData> => get('/room'),
  logDecision: (body: { subject: string; resolution: string }): Promise<{ id: string }> =>
    post('/room/decisions', body),
  openPoll: (body: { question: string; kind: string; options: string[] }): Promise<{ id: string }> =>
    post('/room/polls', body),
  vote: (id: string, response: string): Promise<{ ok: true }> =>
    post(`/room/polls/${id}/vote`, { response }),
  closePoll: (id: string): Promise<{ ok: true }> => post(`/room/polls/${id}/close`),
  pollResult: (id: string): Promise<PollResult> => get(`/room/polls/${id}/result`),

  protect: (): Promise<{
    cards: ProtectCard[];
    counts: { full: number; partial: number; checked: number };
    floor: string;
  }> => get('/protect'),

  // ─── C7 export, and the three doors into a claim's words. Both defined
  // below the object: `exportSelection` needs a 422 to survive as a value.
  exportSelection,
  claimsPaper: (): Promise<ClaimsPaperDoc> => post('/export/paper', {}),
  deleteClaim: (id: string): Promise<unknown> => del(`/claims/${id}`),
  draftClaimText: (id: string, body: { field: 'rationale' | 'wording'; seed?: string }) =>
    post<DraftedText>(`/claims/${id}/draft`, body),
};

/** A document that may leave the tool, with what it is asking to be taken on trust. */
export interface ExportedDocument {
  kind: 'document';
  /** Markdown. Plain enough to paste into an email, print, or open anywhere. */
  document: string;
  unverified: number;
  total: number;
}

/** The one refusal: nothing in the selection has been checked by anyone. */
export interface ExportRefused {
  kind: 'refused';
  reason: string;
}

/**
 * The export, with its refusal carried as a value rather than thrown.
 *
 * The single `request` wrapper above collapses every non-2xx into a thrown
 * Error, which is right for the twenty-odd calls that use it and wrong for
 * exactly one status on exactly one route. A 422 here is not a failure: the
 * server built the document, looked at it, and decided a printout whose every
 * line is unchecked should not exist. Thrown, that decision arrives at the UI
 * indistinguishable from a dropped connection and gets rendered in red as
 * something that went wrong — which is the opposite of what it is, and would
 * teach a rep to press the button again rather than go and check a comparison.
 *
 * So this one duplicates four lines of the wrapper. Everything else about the
 * wrapper's contract — same-origin credentials, JSON error bodies, a thrown
 * Error for a genuine fault — is preserved.
 */
async function exportSelection(
  kind: 'protect' | 'claims' | 'claims-working',
): Promise<ExportedDocument | ExportRefused> {
  const response = await fetch('/api/export', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ kind }),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    document?: string;
    unverified?: number;
    total?: number;
  };

  if (response.status === 422) {
    return { kind: 'refused', reason: payload.error ?? 'There is nothing to export.' };
  }
  if (!response.ok) throw new Error(payload.error ?? `/export failed: ${response.status}`);

  return {
    kind: 'document',
    document: payload.document ?? '',
    unverified: payload.unverified ?? 0,
    total: payload.total ?? 0,
  };
}

/** What the school will say about one claim, and how much trouble the group thinks it is. */
export interface ClaimObjection {
  id: string;
  objection: string;
  counter: string | null;
  /** Null until somebody rates it. An unrated objection is one person's worry. */
  rating: 'weak' | 'answered' | 'fatal' | null;
  draftedBy: string;
  /**
   * Whether the group has weighed it — not who did. `plain.ts` withholds
   * unrated objections from a hundred and twenty people, so this changes what
   * may be shown; the identity behind it is deliberately not sent, because a
   * surface that has the id starts attributing a judgement the group made
   * together.
   */
  rated: boolean;
}

export type RateOutcome = { kind: 'rated' } | { kind: 'refused'; reason: string };

/**
 * Rating an objection, with the server's one refusal carried rather than thrown.
 *
 * `PATCH /objections/:id` answers 422 for exactly one case: rated `answered`
 * with no answer written anywhere. It is the rating that unblocks the claim —
 * the migration 0013 trigger will not endorse a claim carrying an unanswered
 * fatal objection — so accepting an empty one would let anybody clear the gate
 * by relabelling the problem. That is the product working.
 *
 * Thrown, it would arrive at the UI through the same path as a dropped
 * connection and get rendered in `--color-defect` beside the word "error",
 * which teaches a representative to press the button again. The second press
 * fails identically, and the thing that would actually fix it — writing two
 * sentences of answer — is never suggested. Same reasoning, same shape and the
 * same four duplicated lines as `exportSelection` above; every other status
 * still throws, or the opposite mistake appears and a 500 reads as a
 * considered decision.
 */
async function rateObjection(
  id: string,
  body: { rating: 'weak' | 'answered' | 'fatal'; counter?: string },
): Promise<RateOutcome> {
  const response = await fetch(`/api/objections/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as { error?: string };

  if (response.status === 422) {
    return {
      kind: 'refused',
      reason: payload.error ?? 'An objection is not answered until somebody has written the answer.',
    };
  }
  if (!response.ok) throw new Error(payload.error ?? `/objections failed: ${response.status}`);

  return { kind: 'rated' };
}

/** A model's draft of one claim field. Never saved by the server; see draft-claim.ts. */
export interface DraftedText {
  text: string;
  /**
   * Everything the draft treats as true that was not in the material it was
   * given. Shown beside the draft, because a fabricated sentence is
   * indistinguishable from a sourced one by reading.
   */
  assumptions: string[];
  model: string;
  /** True when it developed a person's own starting words rather than starting from nothing. */
  seeded: boolean;
}

export interface PlainData {
  claims: {
    ref: string;
    title: string;
    type: string;
    status: string;
    rationale: string | null;
    costPerFteYear: number | null;
    costMethod: string | null;
    costBasis: 'total_annual' | 'per_affected_teacher' | 'per_fte_year' | 'once' | null;
    costDraftedBy: 'human' | 'ai' | null;
    objections: string[];
    clauses: string[];
  }[];
  protect: {
    number: string;
    heading: string | null;
    exposure: Exposure;
    /** What survives the Agreement. Empty means genuinely nothing. */
    statutoryFloors: { provision: string; gives: string }[];
    plain: string | null;
    entitlements: string[];
  }[];
  protectWaiting: number;
  polls: { id: string; question: string; options: string[]; closed: boolean; turnout: number }[];
  decisions: { occurredOn: string; subject: string; resolution: string }[];
}

export interface RoomData {
  agenda: {
    ref: string;
    title: string;
    type: string;
    status: string;
    priorityRank: number | null;
    redTeamItems: number;
  }[];
  recentDecisions: { id: string; occurredOn: string; subject: string; resolution: string }[];
  polls: {
    id: string;
    question: string;
    kind: string;
    options: string[];
    closesAt: string | null;
    closed: boolean;
    turnout: number;
  }[];
}

export interface PollResult {
  question: string;
  turnout: number;
  closed: boolean;
  results: { option: string; votes: number }[];
  withheld: string | null;
  caveat: string;
}

export interface AskAnswer {
  question: string;
  answer: string | null;
  refusal: string | null;
  citations: string[];
  evidence: {
    number: string;
    heading: string | null;
    instrument: string;
    isPrimary: boolean;
    text: string;
    pageStart: number | null;
  }[];
  model: string | null;
}

export interface BankedAnswer {
  id: string;
  question: string;
  alsoAsked: string[];
  group: string;
  body: string;
  citations: unknown;
}

/**
 * Four states, not two.
 *
 * `full` once meant "the Award is silent" and rendered as "there is no minimum
 * to fall back to". Twelve of the eighteen cards saying that were wrong:
 * severance falls to NES ss.119-123, long service leave to the Long Service
 * Leave Act 1955 (NSW). `statute` is that case; `unchecked` is where nobody has
 * decided, which must not be shown as either.
 */
export type Exposure = 'full' | 'statute' | 'partial' | 'unchecked';

export interface ProtectCard {
  number: string;
  heading: string | null;
  exposure: Exposure;
  statutoryFloors: { entitlement: string; source: string; provision: string; gives: string }[];
  entitlements: {
    type: string;
    quantum: string | null;
    unit: string | null;
    cumulative: boolean | null;
    verified: boolean;
  }[];
  plain: string | null;
  plainVerified: boolean;
  awardFloor: { number: string; heading: string | null } | null;
  deltaNote: string | null;
  comparisonVerified: boolean;
  /** The comparison this card rests on, so the board can be where it is checked. */
  alignmentId: string;
  watch: string | null;
  fullyChecked: boolean;
}

export interface CompareIndexRow {
  number: string;
  heading: string | null;
  adjudicated: number;
  verified: number;
  silent: number;
  ahead: number;
  behind: number;
}

export interface ComparatorCell {
  instrumentId: string;
  shortName: string;
  kind: string;
  verdict: string | null;
  matchedNumber: string | null;
  matchedHeading: string | null;
  matchedText: string | null;
  deltaNote: string | null;
  model: string | null;
  verified: boolean;
  verifiedBy: string | null;
  needsReview: boolean;
}

export interface UnitRow {
  number: string;
  heading: string | null;
  ourText: string;
  comparators: ComparatorCell[];
}

export interface ComparisonView {
  number: string;
  heading: string | null;
  ourText: string;
  ourPlain: string | null;
  plainVerified: boolean;
  artefacts: { label: string; detail: string }[];
  units: UnitRow[];
}

export interface Equivalence {
  ourMinutes: number;
  theirMinutes: number;
  ourNote: string | null;
  theirSource: string | null;
  theirNote: string | null;
  theirName: string | null;
  overshootMinutes: number;
  rows: {
    role: string;
    comparatorPeriods: number;
    minutes: number;
    basis: string;
    ourPeriods: number;
    cite: string;
    category: string | null;
  }[];
}

/**
 * Provenance on a claim row.
 *
 * 'ai_seed' is the value that matters, and it says two things rather than one:
 * this row is fixture data, and a model wrote it. The workbench renders against
 * fourteen such claims — thirteen from the mockup, attributed to seven
 * representatives who do not exist, plus one I made while building the screen
 * that displays them — and until this reached the UI there was nothing to
 * separate any of them from work a colleague had actually done.
 */
export type DraftedBy = 'human' | 'ai' | 'ai_seed';

export interface Adjudication {
  id: string;
  alignmentId: string;
  disposition: 'stands' | 'corrected' | 'withdrawn';
  primary: { number: string; heading: string | null };
  compare: { number: string; heading: string | null; instrument: string } | null;
  settledVerdict: string;
  pipelineVerdict: string;
  settledNote: string | null;
  pipelineNote: string | null;
  suggestedNumber: string | null;
  model: string;
  decidedAt: string;
  woundBack: { at: string; by: string | null; note: string | null } | null;
}

export interface AdjudicationReport {
  adjudications: Adjudication[];
  totals: {
    person: number;
    model: number;
    withdrawn: number;
    unread: number;
    woundBack: number;
  };
}

export interface TurnInput {
  question: string;
  answer: string | null;
  citations: string[];
  drafted?: string | null;
  model?: string | null;
}

export interface SuggestionList {
  behind: {
    kind: 'behind_the_floor';
    alignmentId: string;
    clause: { number: string; heading: string | null };
    against: { instrument: string; number: string | null };
    why: string | null;
    /** The finding survived a second reading that changed the first verdict. */
    reread: boolean;
  }[];
  gaps: {
    kind: 'they_have_it_we_do_not';
    silenceId: string;
    against: { instrument: string; number: string };
    subject: string;
    why: string;
  }[];
  totals: { suggested: number; alreadyClaimed: number };
}

export interface SearchResults {
  results: {
    number: string;
    heading: string | null;
    instrument: string;
    isPrimary: boolean;
    /** Matched words wrapped in << >> so the client can mark them. */
    snippet: string;
  }[];
  total: number;
  shown: number;
}
